import React, { useState, useEffect } from 'react';
import injectStylesheet from 'react-jss';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { vendorAllowed, isMobile } from 'util/index';
import Button from 'components/Button';
import * as cssVariable from 'cssVariables';
import ButtonTypes from 'components/Button/Button.const';
import { trackCTAButton } from 'analytics';
import JukeLogo from 'components/svg/Logo';
import CloseIcon from 'components/svg/Close';
import Triangle from 'components/svg/Triangle';
import { registerForPushSubscription, checkSubscriptionAvailability } from 'services/pushNotificationService';
import { PUSH_NOTIFICATION_POPUP, PUSH_NOTIFICATION_POPUP_TEST_ID } from './PushNotificationPopup.const';

function PushNotificationPopup({ classes }) {
  const [notificationOpen, setNotificationOpen] = useState(true);
  useEffect(() => {
    setNotificationOpen(!window.localStorage.getItem(PUSH_NOTIFICATION_POPUP.LOCAL_STORAGE_NAME));
  }, []);

  const onClose = () => {
    trackCTAButton('click', 'close', 'click_notification_popup');
    setNotificationOpen(false);
    window.localStorage.setItem(PUSH_NOTIFICATION_POPUP.LOCAL_STORAGE_NAME, true);
  };

  const subscribeToWebPush = () => {
    trackCTAButton('click', 'subscribe', 'click_notification_popup');
    registerForPushSubscription();
    setNotificationOpen(false);
    window.localStorage.setItem(PUSH_NOTIFICATION_POPUP.LOCAL_STORAGE_NAME, true);
  };

  if (!notificationOpen || !checkSubscriptionAvailability() || !vendorAllowed(VENDORS.LEANPLUM)) return null;

  return (
    <div
      className={classNames(classes.wrapper, {
        [classes.mobileWrapper]: isMobile,
      })}
      data-testid={PUSH_NOTIFICATION_POPUP_TEST_ID.NOTIFICATION_POPUP}
    >
      <button
        type="button"
        className={classes.close}
        data-testid={PUSH_NOTIFICATION_POPUP_TEST_ID.CLOSE_ICON}
        onClick={onClose}
      >
        <CloseIcon className={classes.closeIcon} />
      </button>
      <Triangle className={classes.triangle} color={cssVariable.white} />
      <div className={classes.leftContainer}>
        <JukeLogo className={classes.logo} />
      </div>
      <div className={classes.rightContainer}>
        <p
          className={classes.text}
          dangerouslySetInnerHTML={{
            __html: PUSH_NOTIFICATION_POPUP.CONTENT,
          }}
        />
        <Button
          className={classes.button}
          type={ButtonTypes.SECONDARY}
          data-testid={PUSH_NOTIFICATION_POPUP_TEST_ID.CLOSE_BUTTON}
          onClick={onClose}
          text={PUSH_NOTIFICATION_POPUP.CLOSE_BUTTON_TEXT}
        />
        <Button
          className={classes.button}
          type={ButtonTypes.PRIMARY}
          data-testid={PUSH_NOTIFICATION_POPUP_TEST_ID.SUBSCRIBE_BUTTON}
          onClick={subscribeToWebPush}
          text={PUSH_NOTIFICATION_POPUP.SUBSCRIBE_BUTTON_TEXT}
        />
      </div>
    </div>
  );
}

const styles = (theme) => ({
  '@keyframes sideIn': {
    to: {
      top: 33,
    },
  },

  wrapper: {
    position: 'absolute',
    left: 50,
    top: -300,
    width: 728,
    backgroundColor: cssVariable.white,
    display: 'flex',
    boxShadow: '0 23px 52px 0 rgba(0,0,0,0.4)',
    animation: `$sideIn 1s ease-out ${PUSH_NOTIFICATION_POPUP.SLIDE_IN_DELAY}ms 1 forwards`,
    zIndex: theme.zIndex.pushNotification, // is needed to be above the smartbanner
  },

  triangle: {
    position: 'absolute',
    top: -30,
    left: 40,
    width: 65,
    height: 'auto',
  },

  close: {
    position: 'absolute',
    top: 20,
    right: 20,
    background: 'none',
    border: 'none',
    padding: 0,
    color: cssVariable.grey500,

    '&:hover': {
      cursor: 'pointer',
    },
  },

  closeIcon: {
    width: 16,
    height: 16,
  },

  leftContainer: {
    padding: [0, 65],
    display: 'flex',
  },

  logo: {
    width: 155,
  },

  rightContainer: {
    padding: [60, 80, 25, 0],
  },

  text: {
    fontWeight: cssVariable.fontWeights.light,
    fontSize: cssVariable.fontSizes.mediumLarge,
    marginTop: 0,
  },

  button: {
    marginRight: 10,
    fontSize: cssVariable.fontSizes.medium,
    textTransform: 'none',
    marginTop: 15,
  },

  [theme.breakpoints.down('md')]: {
    wrapper: {
      width: 628,
    },
    rightContainer: {
      padding: [60, 20, 25, 0],
    },

    leftContainer: {
      padding: [0, 35],
    },

    button: {
      fontSize: cssVariable.fontSizes.small,
    },
  },

  [theme.breakpoints.down('sm')]: {
    wrapper: {
      width: 428,
    },

    leftContainer: {
      display: 'none',
    },

    rightContainer: {
      padding: 25,
    },

    close: {
      top: 10,
      right: 10,
    },

    logo: {
      width: 95,
    },
  },

  mobileWrapper: {
    position: 'fixed',
    width: 'calc(100% - 20px)',
    left: 10,

    '& $text': {
      fontSize: 16,
    },

    '& $button': {
      marginRight: 5,
      marginTop: 0,
      padding: [0, 5],
    },

    '& $triangle': {
      display: 'none',
    },
  },
});

PushNotificationPopup.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default injectStylesheet(styles)(PushNotificationPopup);
